<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import MotorReservaConfigAPI from 'dashboard/api/motorReservaConfig';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const isSubmitting = ref(false);
const activeTab = ref('general');
const amenitySearch = ref('');

const config = computed(() => getters['motorReservaConfig/getConfig'].value);
const uiFlags = computed(() => getters['motorReservaConfig/getUIFlags'].value);

const form = ref({
  property_name: '',
  property_description: '',
  property_website: '',
  property_phone: '',
  property_phone_secondary: '',
  property_whatsapp: '',
  property_email: '',
  youtube_url: '',
  sales_contact_name: '',
  sales_contact_email: '',
  sales_contact_phone: '',
  facebook_url: '',
  instagram_handle: '',
  address_cep: '',
  address_street: '',
  address_neighborhood: '',
  address_city: '',
  address_state: '',
  address_country: 'Brasil',
  map_embed_url: '',
  map_share_url: '',
  timezone: 'America/Sao_Paulo',
  currency: 'BRL',
  check_in_time: '15:00',
  check_out_time: '12:00',
  general_policy: '',
  min_advance_hours: 24,
  max_advance_days: 365,
  cancellation_policy: '',
  terms_and_conditions: '',
  privacy_policy: '',
  lgpd_enabled: false,
  property_amenities: [],
  // Idade
  age_policy_mode: 'checkin_date',
  age_ranges: [
    { min_age: 0, max_age: 4, charge_type: 'free' },
    { min_age: 5, max_age: 12, charge_type: 'adult' },
    { min_age: 13, max_age: 17, charge_type: 'adult' },
  ],
  free_children_per_adult: 2,
  free_adults_required: 2,
  // Pagamento
  credit_card_enabled: false,
  credit_card_installments: 10,
  credit_card_min_installment: 200,
  accepted_card_brands: [],
  include_extras_in_card: false,
  antifraud_enabled: false,
  deposit_enabled: false,
  pix_enabled: false,
  include_extras_in_pix: false,
  pix_min_advance_days: 0,
  pix_qr_expiration_minutes: 30,
  direct_hotel_enabled: false,
  // Gateway Asaas
  asaas_api_key: '',
  asaas_environment: 'production',
  // Integrações
  integrations_config: {
    gtag_id: '',
    gtm_id: '',
    facebook_pixel_id: '',
    universal_script: '',
    tripadvisor_enabled: false,
    tripadvisor_partner_id: '',
    tripadvisor_hotel_id: '',
    trivago_enabled: false,
    trivago_partner_id: '',
    trivago_hotel_id: '',
  },
  // Taxas
  taxes_config: [],
  // Design
  design_config: {
    bg_color: 'transparent',
    mobile_color: '#18569b',
    primary_color: '#18569b',
    center_logo: false,
    show_shadow: false,
    show_header: true,
    show_footer: true,
    show_calendar_price: true,
    show_calendar_offers: true,
  },
  no_availability_text: '',
  enabled_languages: ['pt'],
  // Orçamento na Conversa
  orcamento_conversa_enabled: false,
  orcamento_message_template: '',
});

const tabs = [
  { key: 'general', label: 'Dados Gerais' },
  { key: 'photos', label: 'Fotos' },
  { key: 'policy', label: 'Política Geral' },
  { key: 'privacy', label: 'Privacidade' },
  { key: 'amenities', label: 'Comodidades' },
  { key: 'age', label: 'Políticas de Idade' },
  { key: 'payment', label: 'Meios de Pagamento' },
  { key: 'integrations', label: 'Integrações' },
  { key: 'taxes', label: 'Taxas' },
  { key: 'orcamento', label: 'Orçamento Conversa' },
];

const cardBrands = [
  'VISA',
  'MASTERCARD',
  'ELO',
  'AMEX',
  'DINERS',
  'DISCOVER',
  'AURA',
  'JCB',
  'MAESTRO',
  'HIPER',
  'HIPERCARD',
];

const chargeTypes = [
  { value: 'free', label: 'Gratuito' },
  { value: 'child', label: 'Criança' },
  { value: 'adult', label: 'Adulto' },
  { value: 'free_adult', label: 'Gratuito + Adulto' },
];

const agePolicyModes = [
  {
    value: 'checkin_date',
    label: 'Considerar as políticas de idade pela data de Check-in',
  },
  {
    value: 'booking_date',
    label: 'Considerar as políticas de idade pela data da Reserva',
  },
];

// Comodidades organizadas por categoria
const amenityCategories = {
  'Áreas de Lazer': [
    'Piscina climatizada',
    'Piscina aquecida',
    'Piscina coberta',
    'Piscina infantil',
    'Piscina adulto',
    'Piscina ao ar livre',
    'Piscina de borda infinita',
    'Piscina de ondas',
    'Toboágua',
    'Brinquedos Molhados',
    'Hidromassagem Casal',
    'Hidromassagem Solteiro',
    'Jacuzzi',
    'Ofurô',
    'Sauna seca',
    'Sauna a vapor',
    'Sauna',
    'Spa',
    'Solário',
    'Redário',
    'Playground',
    'Brinquedoteca',
    'Espaço Kids',
    'Casa na árvore',
    'Lareira ao ar livre',
    'Cinema',
    'Sala de Leitura',
    'Lago',
    'Lago ornamental',
    'Cachoeira',
    'Rio',
    'Praia privativa',
    'Banho de Espuma na piscina',
    'Ducha externa',
    'Deck com Espreguiçadeiras',
    'Área de Fogueira',
    'Wine Garden',
    'Espaço de Convivência',
    'Gazebo',
    'Bicicletário',
    'Horta orgânica',
    'Mini Fazenda',
    'Viveiros',
    'Pomar',
    'Área para piquenique',
    'Rooftop',
    'Mirante',
    'Contato com a natureza',
    'Vista Incrível',
    'Jardins Europeus',
  ].sort(),
  Internet: [
    'Acesso gratuito de Wi-Fi',
    'Wi-Fi gratuito nas áreas comuns',
    'Acesso Wi-Fi disponível em toda propriedade',
    'Wi-Fi gratuito nos quartos',
  ].sort(),
  Atividades: [
    'Academia',
    'Quadra de Tênis',
    'Quadra Poliesportiva',
    'Quadra de Beach tennis',
    'Campo de Futebol',
    'Campo de Futebol Society',
    'Volei de Areia',
    'Tênis de Mesa/Pingue-pongue',
    'Bilhar',
    'Snooker Oficial',
    'Pebolim',
    'Jogo de Dardos',
    'Boliche',
    'Sala de Jogos',
    'Sala de TV',
    'Caiaque',
    'Stand-Up',
    'Pedalinho',
    'Passeio de Barco',
    'Canoagem',
    'Mergulho',
    'Windsurf',
    'Pescaria',
    'Frescobol',
    'Tirolesa',
    'Arvorismo',
    'Escalada',
    'Rafting',
    'Sky Bike',
    'Passeio a Cavalo',
    'Passeio de 4x4',
    'Quadriciclo',
    'Trilhas ecológicas',
    'Trilhas a pé',
    'Ciclismo',
    'Empréstimo Bicicletas',
    'Aluguel de bicicletas',
    'Aula de Yoga',
    'Minigolfe',
    'Campo de Golf',
    'Paintball',
    'Campo de bocha',
    'Squash',
    'Patinete',
    'Balonismo',
    'Telescópio',
    'Fogão a lenha',
  ].sort(),
  'Alimentos e Bebidas': [
    'Restaurante',
    'Restaurante (à la carte)',
    'Restaurante (buffet)',
    'Restaurante colonial',
    'Bar',
    'Bar molhado',
    'Lanchonete',
    'Cozinha comunitária',
    'Churrasqueira',
    'Churrasqueira coletiva',
    'Espaço Gourmet',
    'Café da manhã cortesia',
    'Café da Manhã Incluso na Diária',
    'Pensão Completa',
    'Serviço de Quarto',
    'Serviço de Jantar',
    'Cozinha',
    'Loja de Conveniência',
    'Adega de Vinhos',
    'Degustação guiada de vinhos e queijos',
    'Água Mineral Cortesia',
  ].sort(),
  Gerais: [
    'Estacionamento gratuito',
    'Estacionamento coberto',
    'Estacionamento',
    'Jardim',
    'Elevador',
    'Quartos para famílias',
    'Quartos para não fumantes',
    'Acessibilidade',
    'Aceita pets',
    'Ar-condicionado',
    'Aquecimento',
    'Aquecimento solar',
    'Voltagem 110V',
    'Voltagem 220V',
    'Suíte Lua de Mel',
    'Suíte nupcial',
    'Cama Elástica para Crianças',
    'Mini Fazenda',
    'Sala de reuniões/eventos',
    'Coworking',
    'Lareira em área interna',
    'Câmeras de Vigilância 24h',
    'Carregador Elétrico',
    'Energia Solar',
    'Lobby',
    'Roupas de Cama e Banho',
    'Toalhas de praia e piscina',
    'Berços para crianças (mediante pedido)',
  ].sort(),
  Serviços: [
    'Recepção 24h',
    'Portaria 24 horas',
    'Concierge',
    'Lavanderia',
    'Serviços de massagem',
    'Serviço de Transfer',
    'Transfer aeroporto',
    'Balcão de turismo',
    'Organização de Passeios',
    'Manobrista',
    'Cofre',
    'Depósito de bagagens',
    'Aluguel de carros',
    'Serviço de câmbio',
    'Serviço de limpeza diário',
    'Recreação',
    'Segurança 24h',
    'Pet Place',
    'Salão de Beleza',
    'Mini Mercado',
  ].sort(),
  'Idiomas Falados': [
    'Português',
    'Inglês',
    'Espanhol',
    'Francês',
    'Alemão',
    'Italiano',
    'Japonês',
    'Russo',
    'Hebraico',
    'Grego',
  ],
};

const filteredCategories = computed(() => {
  if (!amenitySearch.value.trim()) return amenityCategories;
  const q = amenitySearch.value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  const result = {};
  Object.entries(amenityCategories).forEach(([cat, items]) => {
    const filtered = items.filter(a =>
      a
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .includes(q)
    );
    if (filtered.length) result[cat] = filtered;
  });
  return result;
});

const toggleAmenity = amenity => {
  const i = form.value.property_amenities.indexOf(amenity);
  if (i >= 0) form.value.property_amenities.splice(i, 1);
  else form.value.property_amenities.push(amenity);
};

const toggleCardBrand = brand => {
  const i = form.value.accepted_card_brands.indexOf(brand);
  if (i >= 0) form.value.accepted_card_brands.splice(i, 1);
  else form.value.accepted_card_brands.push(brand);
};

const addAgeRange = () => {
  form.value.age_ranges.push({ min_age: 0, max_age: 0, charge_type: 'adult' });
};
const removeAgeRange = idx => form.value.age_ranges.splice(idx, 1);

const addTax = () => {
  form.value.taxes_config.push({
    name: '',
    tax_type: '',
    percentage: 0,
    included_in_rate: false,
    value_format: 'fixed',
    description: '',
    per_night_or_stay: 'stay',
    per_room_or_person: 'room',
    charge_in_prepayment: false,
    enabled: true,
  });
};
const removeTax = idx => form.value.taxes_config.splice(idx, 1);

const loadConfig = () => {
  if (config.value && config.value.id) {
    const c = config.value;
    Object.keys(form.value).forEach(key => {
      if (c[key] !== undefined && c[key] !== null) {
        if (Array.isArray(c[key])) form.value[key] = [...c[key]];
        else if (typeof c[key] === 'object')
          form.value[key] = { ...form.value[key], ...c[key] };
        else form.value[key] = c[key];
      }
    });
  }
};

const saveConfig = async () => {
  isSubmitting.value = true;
  try {
    await store.dispatch('motorReservaConfig/update', {
      motor_reserva_config: form.value,
    });
    useAlert('Configurações salvas com sucesso.');
  } catch (error) {
    useAlert(error?.message || 'Erro ao salvar configurações.');
  } finally {
    isSubmitting.value = false;
  }
};

const webhookUrl = computed(() => {
  const base = window.location.origin;
  return `${base}/webhooks/asaas`;
});

// Asaas connection test
const asaasTestLoading = ref(false);
const asaasTestResult = ref(null); // null | 'success' | 'error'
const asaasTestError = ref('');
const asaasTestData = ref(null);

const testAsaasConnection = async () => {
  const key = form.value.asaas_api_key;
  if (!key) {
    useAlert('Informe a chave de API do Asaas antes de testar.');
    return;
  }
  asaasTestLoading.value = true;
  asaasTestResult.value = null;
  asaasTestError.value = '';
  asaasTestData.value = null;
  try {
    const { data } = await MotorReservaConfigAPI.testAsaas(
      key,
      form.value.asaas_environment
    );
    asaasTestResult.value = 'success';
    asaasTestData.value = data;
  } catch (error) {
    asaasTestResult.value = 'error';
    asaasTestError.value =
      error?.response?.data?.message || error?.message || 'Erro ao conectar.';
  } finally {
    asaasTestLoading.value = false;
  }
};

const copyBookingUrl = async () => {
  try {
    await navigator.clipboard.writeText(config.value.booking_url);
    useAlert('Link copiado!');
  } catch {
    useAlert('Não foi possível copiar o link.');
  }
};

// Photo management — três coleções: logo, capa (banner), galeria
const isUploadingCover = ref(false);
const isUploadingGallery = ref(false);
const isUploadingLogo = ref(false);
const coverFileInput = ref(null);
const galleryFileInput = ref(null);
const logoFileInput = ref(null);

const coverPhotos = computed(() => config.value?.property_photo_urls || []);
const galleryPhotos = computed(() => config.value?.gallery_photo_urls || []);
const logoUrl = computed(
  () => config.value?.logo_file_url || config.value?.property_logo || null
);

const triggerCoverUpload = () => coverFileInput.value?.click();
const triggerGalleryUpload = () => galleryFileInput.value?.click();
const triggerLogoUpload = () => logoFileInput.value?.click();

const uploadFiles = async (files, kind, flagRef, inputRef) => {
  if (!files || files.length === 0) return;
  flagRef.value = true;
  try {
    for (let i = 0; i < files.length; i++) {
      await MotorReservaConfigAPI.addPhoto(files[i], kind);
    }
    await store.dispatch('motorReservaConfig/get');
    useAlert('Imagem enviada com sucesso.');
  } catch (error) {
    useAlert(error?.message || 'Erro ao enviar imagem.');
  } finally {
    flagRef.value = false;
    if (inputRef.value) inputRef.value.value = '';
  }
};

const handleCoverFiles = e =>
  uploadFiles(e.target.files, 'cover', isUploadingCover, coverFileInput);
const handleGalleryFiles = e =>
  uploadFiles(e.target.files, 'gallery', isUploadingGallery, galleryFileInput);
const handleLogoFile = e =>
  uploadFiles(e.target.files, 'logo', isUploadingLogo, logoFileInput);

const removeCoverPhoto = async photoId => {
  try {
    await MotorReservaConfigAPI.removePhoto(photoId, 'cover');
    await store.dispatch('motorReservaConfig/get');
  } catch (error) {
    useAlert(error?.message || 'Erro ao remover foto.');
  }
};

const removeGalleryPhoto = async photoId => {
  try {
    await MotorReservaConfigAPI.removePhoto(photoId, 'gallery');
    await store.dispatch('motorReservaConfig/get');
  } catch (error) {
    useAlert(error?.message || 'Erro ao remover foto.');
  }
};

const clearLogo = async () => {
  try {
    await MotorReservaConfigAPI.clearLogo();
    await store.dispatch('motorReservaConfig/get');
  } catch (error) {
    useAlert(error?.message || 'Erro ao remover logo.');
  }
};

onBeforeMount(async () => {
  await store.dispatch('motorReservaConfig/get');
  loadConfig();
});

const ic =
  'w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 focus:border-woot-500 focus:outline-none';
</script>

<template>
  <div class="flex flex-col gap-4">
    <div
      v-if="uiFlags.isFetching"
      class="flex items-center justify-center py-8"
    >
      <span class="text-slate-500">Carregando...</span>
    </div>
    <template v-else>
      <div class="flex items-center justify-between">
        <h3 class="text-base font-medium text-slate-800 dark:text-slate-100">
          Configurando o hotel
        </h3>
        <Button
          label="Salvar Configurações"
          :is-loading="isSubmitting"
          @click="saveConfig"
        />
      </div>

      <div
        class="flex gap-1 border-b border-slate-200 dark:border-slate-600 overflow-x-auto"
      >
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          class="px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap"
          :class="
            activeTab === tab.key
              ? 'border-woot-500 text-woot-500'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          "
          @click="activeTab = tab.key"
        >
          {{ tab.label }}
        </button>
      </div>

      <!-- DADOS GERAIS -->
      <div
        v-show="activeTab === 'general'"
        class="flex flex-col gap-4 max-w-3xl"
      >
        <!-- Identificador + Link público -->
        <div
          v-if="config && config.booking_identifier"
          class="border-2 border-woot-500/30 bg-woot-500/5 dark:bg-slate-800 rounded-lg p-4 flex flex-col gap-3"
        >
          <div class="flex items-center justify-between">
            <h4
              class="text-sm font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2"
            >
              <span class="w-2 h-2 rounded-full bg-green-500 inline-block" />
              Identificador do Motor de Reserva
            </h4>
            <span
              class="font-mono text-xs bg-slate-200 dark:bg-slate-700 px-2 py-1 rounded text-slate-700 dark:text-slate-200"
            >
              {{ config.booking_identifier }}
            </span>
          </div>
          <div class="flex flex-col gap-1">
            <label
              class="text-xs font-medium text-slate-500 dark:text-slate-400"
            >
              Link de acesso para o cliente
            </label>
            <div class="flex items-center gap-2">
              <input
                :value="config.booking_url"
                readonly
                :class="ic"
                class="flex-1 !bg-slate-50 dark:!bg-slate-900 font-mono text-xs cursor-text"
                @focus="$event.target.select()"
              />
              <Button size="small" label="Copiar" @click="copyBookingUrl" />
            </div>
            <a
              :href="config.booking_url"
              target="_blank"
              class="text-xs text-woot-500 hover:text-woot-600 mt-1 inline-flex items-center gap-1"
            >
              Abrir página de reservas &nearr;
            </a>
          </div>
        </div>
        <div
          v-else-if="config && config.id && !config.booking_identifier"
          class="border border-yellow-300 dark:border-yellow-600 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 text-sm text-yellow-700 dark:text-yellow-300"
        >
          Clique em &ldquo;Salvar Configurações&rdquo; para gerar o
          identificador e o link de reservas.
        </div>
        <div
          v-else-if="!config || !config.id"
          class="border border-slate-200 dark:border-slate-600 rounded-lg p-4 text-sm text-slate-500"
        >
          Salve as configurações para gerar o identificador e o link de
          reservas.
        </div>

        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
            >Nome da propriedade</label>
          <input v-model="form.property_name" type="text" :class="ic" />
        </div>
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
            >Texto Descritivo</label>
          <textarea v-model="form.property_description" rows="6" :class="ic" />
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
              >URL Página Hotel</label>
            <input v-model="form.property_website" type="url" :class="ic" />
          </div>
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
              >YouTube</label>
            <input v-model="form.youtube_url" type="url" :class="ic" />
          </div>
        </div>
        <div class="grid grid-cols-3 gap-4">
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
              >Telefone Principal</label>
            <input v-model="form.property_phone" type="text" :class="ic" />
          </div>
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
              >Telefone Secundário</label>
            <input
              v-model="form.property_phone_secondary"
              type="text"
              :class="ic"
            />
          </div>
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
              >WhatsApp</label>
            <input v-model="form.property_whatsapp" type="text" :class="ic" />
          </div>
        </div>
        <div
          class="border border-slate-200 dark:border-slate-600 rounded-lg p-4"
        >
          <h4
            class="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3"
          >
            Responsável por Vendas Online
          </h4>
          <div class="grid grid-cols-3 gap-4">
            <div>
              <label class="block text-xs text-slate-500 mb-1">Nome</label><input
                v-model="form.sales_contact_name"
                type="text"
                :class="ic"
              />
            </div>
            <div>
              <label class="block text-xs text-slate-500 mb-1">Email</label><input
                v-model="form.sales_contact_email"
                type="email"
                :class="ic"
              />
            </div>
            <div>
              <label class="block text-xs text-slate-500 mb-1">Telefone/Celular</label><input
                v-model="form.sales_contact_phone"
                type="text"
                :class="ic"
              />
            </div>
          </div>
        </div>
        <div
          class="border border-slate-200 dark:border-slate-600 rounded-lg p-4"
        >
          <h4
            class="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3"
          >
            Redes Sociais
          </h4>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-xs text-slate-500 mb-1">Facebook</label><input v-model="form.facebook_url" type="url" :class="ic" />
            </div>
            <div>
              <label class="block text-xs text-slate-500 mb-1">Instagram</label><input
                v-model="form.instagram_handle"
                type="text"
                :class="ic"
                placeholder="@seuhotel"
              />
            </div>
          </div>
        </div>
        <div
          class="border border-slate-200 dark:border-slate-600 rounded-lg p-4"
        >
          <h4
            class="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3"
          >
            Endereço
          </h4>
          <div class="grid grid-cols-3 gap-4 mb-3">
            <div>
              <label class="block text-xs text-slate-500 mb-1">CEP</label><input v-model="form.address_cep" type="text" :class="ic" />
            </div>
            <div class="col-span-2">
              <label class="block text-xs text-slate-500 mb-1">Logradouro</label><input v-model="form.address_street" type="text" :class="ic" />
            </div>
          </div>
          <div class="grid grid-cols-4 gap-4 mb-3">
            <div>
              <label class="block text-xs text-slate-500 mb-1">Bairro</label><input
                v-model="form.address_neighborhood"
                type="text"
                :class="ic"
              />
            </div>
            <div>
              <label class="block text-xs text-slate-500 mb-1">Cidade</label><input v-model="form.address_city" type="text" :class="ic" />
            </div>
            <div>
              <label class="block text-xs text-slate-500 mb-1">Estado</label><input v-model="form.address_state" type="text" :class="ic" />
            </div>
            <div>
              <label class="block text-xs text-slate-500 mb-1">País</label><input v-model="form.address_country" type="text" :class="ic" />
            </div>
          </div>
          <div class="flex flex-col gap-3">
            <div>
              <label class="block text-xs text-slate-500 mb-1">URL do mapa para embed (usado no motor de reservas)</label>
              <textarea v-model="form.map_embed_url" rows="2" :class="ic" />
              <span class="text-xs text-slate-400">Link iframe src — usado para exibir o mapa na página
                pública.</span>
            </div>
            <div>
              <label class="block text-xs text-slate-500 mb-1">URL do Google Maps compartilhável (usado nos e-mails aos
                hóspedes)</label>
              <input v-model="form.map_share_url" type="url" :class="ic" />
            </div>
          </div>
        </div>

        <!-- DESIGN DO MOTOR -->
        <div
          class="border border-slate-200 dark:border-slate-600 rounded-lg p-4"
        >
          <h4
            class="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3"
          >
            Design do Motor de Reservas
          </h4>
          <div class="grid grid-cols-3 gap-4 mb-3">
            <div>
              <label class="block text-xs text-slate-500 mb-1">Cor de Fundo (Logo)</label>
              <div class="flex gap-2">
                <input
                  v-model="form.design_config.bg_color"
                  type="text"
                  :class="ic"
                />
                <input
                  v-model="form.design_config.bg_color"
                  type="color"
                  class="w-10 h-10 rounded border border-slate-200 cursor-pointer"
                />
              </div>
            </div>
            <div>
              <label class="block text-xs text-slate-500 mb-1">Cor para Mobile</label>
              <div class="flex gap-2">
                <input
                  v-model="form.design_config.mobile_color"
                  type="text"
                  :class="ic"
                />
                <input
                  v-model="form.design_config.mobile_color"
                  type="color"
                  class="w-10 h-10 rounded border border-slate-200 cursor-pointer"
                />
              </div>
            </div>
            <div>
              <label class="block text-xs text-slate-500 mb-1">Cor Principal</label>
              <div class="flex gap-2">
                <input
                  v-model="form.design_config.primary_color"
                  type="text"
                  :class="ic"
                />
                <input
                  v-model="form.design_config.primary_color"
                  type="color"
                  class="w-10 h-10 rounded border border-slate-200 cursor-pointer"
                />
              </div>
            </div>
          </div>
          <div class="flex flex-col gap-2">
            <label
              class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
            >
              <input
                v-model="form.design_config.center_logo"
                type="checkbox"
                class="rounded border-slate-300 text-woot-500"
              />
              Centralizar Logo</label>
            <label
              class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
            >
              <input
                v-model="form.design_config.show_shadow"
                type="checkbox"
                class="rounded border-slate-300 text-woot-500"
              />
              Mostrar Box-Shadow (Logo)</label>
            <label
              class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
            >
              <input
                v-model="form.design_config.show_header"
                type="checkbox"
                class="rounded border-slate-300 text-woot-500"
              />
              Mostrar Cabeçalho (Cover)</label>
            <label
              class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
            >
              <input
                v-model="form.design_config.show_footer"
                type="checkbox"
                class="rounded border-slate-300 text-woot-500"
              />
              Mostrar informações do hotel (Rodapé)</label>
            <label
              class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
            >
              <input
                v-model="form.design_config.show_calendar_price"
                type="checkbox"
                class="rounded border-slate-300 text-woot-500"
              />
              Exibir o melhor preço para o dia no calendário</label>
            <label
              class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
            >
              <input
                v-model="form.design_config.show_calendar_offers"
                type="checkbox"
                class="rounded border-slate-300 text-woot-500"
              />
              Mostrar valores com ofertas aplicadas</label>
          </div>
          <div class="mt-3">
            <label class="block text-xs text-slate-500 mb-1">Texto para Sem Disponibilidade</label>
            <textarea
              v-model="form.no_availability_text"
              rows="3"
              :class="ic"
              placeholder="Variáveis: {{FonePrincipal}} {{FoneSecundario}} {{EmailReserva}} {{NomeDoHotel}}"
            />
          </div>
        </div>

        <!-- IDIOMAS -->
        <div
          class="border border-slate-200 dark:border-slate-600 rounded-lg p-4"
        >
          <h4
            class="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3"
          >
            Idiomas Disponíveis
          </h4>
          <div class="flex flex-col gap-2">
            <label
              class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
            >
              <input
                type="checkbox"
                checked
                disabled
                class="rounded border-slate-300 text-woot-500"
              />
              Português (sempre ativo)</label>
            <label
              class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
            >
              <input
                v-model="form.enabled_languages"
                type="checkbox"
                value="en"
                class="rounded border-slate-300 text-woot-500"
              />
              Inglês</label>
            <label
              class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
            >
              <input
                v-model="form.enabled_languages"
                type="checkbox"
                value="es"
                class="rounded border-slate-300 text-woot-500"
              />
              Espanhol</label>
          </div>
        </div>
      </div>

      <!-- FOTOS -->
      <div
        v-show="activeTab === 'photos'"
        class="flex flex-col gap-6 max-w-3xl"
      >
        <!-- LOGO -->
        <section class="flex flex-col gap-3">
          <div class="flex items-center gap-3">
            <Button
              :label="logoUrl ? 'TROCAR LOGO' : 'DEFINIR LOGO'"
              :is-loading="isUploadingLogo"
              @click="triggerLogoUpload"
            />
            <span
              class="text-xs text-slate-400"
              title="Logo exibida sobre o banner do motor de reservas"
            >
              ⓘ Logo exibida sobre o banner do motor de reservas
            </span>
          </div>
          <input
            ref="logoFileInput"
            type="file"
            accept="image/*"
            class="hidden"
            @change="handleLogoFile"
          />
          <div v-if="logoUrl" class="flex flex-col items-start gap-2">
            <div
              class="p-3 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-600"
            >
              <img
                :src="logoUrl"
                alt="Logo"
                class="max-w-[160px] max-h-[120px] object-contain"
              />
            </div>
            <button
              type="button"
              class="px-3 py-1.5 text-xs font-semibold uppercase rounded bg-red-500 hover:bg-red-600 text-white"
              @click="clearLogo"
            >
              Limpar Logo
            </button>
          </div>
          <p v-else class="text-xs text-slate-400">
            Nenhuma logo enviada.
          </p>
        </section>

        <hr class="border-slate-200 dark:border-slate-600" />

        <!-- FOTOS DE CAPA (BANNER) -->
        <section class="flex flex-col gap-3">
          <div class="flex items-center gap-3">
            <Button
              label="ADICIONAR NOVA FOTO DE CAPA"
              :is-loading="isUploadingCover"
              @click="triggerCoverUpload"
            />
            <span
              class="text-xs text-slate-400"
              title="Estas fotos aparecem no banner principal (slideshow no topo da página)"
            >
              ⓘ Estas fotos aparecem no banner principal (slideshow no topo)
            </span>
          </div>
          <input
            ref="coverFileInput"
            type="file"
            accept="image/*"
            multiple
            class="hidden"
            @change="handleCoverFiles"
          />
          <div
            v-if="coverPhotos.length > 0"
            class="flex flex-wrap gap-3"
          >
            <div
              v-for="photo in coverPhotos"
              :key="photo.id"
              class="flex flex-col gap-1.5"
            >
              <div
                class="rounded overflow-hidden border border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 w-44 h-24"
              >
                <img
                  :src="photo.url"
                  class="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <button
                type="button"
                class="px-3 py-1 text-xs font-semibold uppercase rounded bg-red-500 hover:bg-red-600 text-white"
                @click="removeCoverPhoto(photo.id)"
              >
                Remover
              </button>
            </div>
          </div>
          <p
            v-else-if="!isUploadingCover"
            class="text-xs text-slate-400"
          >
            Nenhuma foto de capa enviada ainda.
          </p>
        </section>

        <hr class="border-slate-200 dark:border-slate-600" />

        <!-- FOTOS DA GALERIA -->
        <section class="flex flex-col gap-3">
          <div class="flex items-center gap-3">
            <Button
              label="ADICIONAR NOVA FOTO NA GALERIA"
              :is-loading="isUploadingGallery"
              @click="triggerGalleryUpload"
            />
            <span
              class="text-xs text-slate-400"
              title="Aparecem na aba Galeria de Fotos da página pública"
            >
              ⓘ Aparecem na aba "Galeria de fotos" da página pública
            </span>
          </div>
          <input
            ref="galleryFileInput"
            type="file"
            accept="image/*"
            multiple
            class="hidden"
            @change="handleGalleryFiles"
          />
          <div
            v-if="galleryPhotos.length > 0"
            class="grid grid-cols-3 gap-3"
          >
            <div
              v-for="photo in galleryPhotos"
              :key="photo.id"
              class="relative group rounded-lg overflow-hidden border border-slate-200 dark:border-slate-600 aspect-video bg-slate-100 dark:bg-slate-800"
            >
              <img
                :src="photo.url"
                class="w-full h-full object-cover"
                loading="lazy"
              />
              <button
                class="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-7 h-7 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity shadow"
                @click.stop="removeGalleryPhoto(photo.id)"
              >
                X
              </button>
            </div>
          </div>
          <p
            v-else-if="!isUploadingGallery"
            class="text-xs text-slate-400"
          >
            Nenhuma foto na galeria ainda.
          </p>
        </section>
      </div>

      <!-- POLÍTICA GERAL -->
      <div
        v-show="activeTab === 'policy'"
        class="flex flex-col gap-4 max-w-3xl"
      >
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
              >Horário do check-in</label><input v-model="form.check_in_time" type="time" :class="ic" />
          </div>
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
              >Horário do check-out</label><input v-model="form.check_out_time" type="time" :class="ic" />
          </div>
        </div>
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
            >Política Geral</label><textarea v-model="form.general_policy" rows="6" :class="ic" />
        </div>
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
            >Política de Cancelamento</label><textarea v-model="form.cancellation_policy" rows="4" :class="ic" />
        </div>
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
            >Termos e Condições</label><textarea v-model="form.terms_and_conditions" rows="4" :class="ic" />
        </div>
      </div>

      <!-- PRIVACIDADE -->
      <div
        v-show="activeTab === 'privacy'"
        class="flex flex-col gap-4 max-w-3xl"
      >
        <label
          class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
        >
          <input
            v-model="form.lgpd_enabled"
            type="checkbox"
            class="rounded border-slate-300 text-woot-500"
          />
          Ativar conformidade LGPD</label>
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
            >Política de Privacidade</label><textarea v-model="form.privacy_policy" rows="8" :class="ic" />
        </div>
      </div>

      <!-- COMODIDADES -->
      <div
        v-show="activeTab === 'amenities'"
        class="flex flex-col gap-3 max-w-3xl"
      >
        <p class="text-sm text-slate-600 dark:text-slate-300">
          Comodidades da propriedade.
          <span class="font-medium text-woot-500">({{ form.property_amenities.length }}) selecionadas</span>
        </p>
        <input
          v-model="amenitySearch"
          type="text"
          :class="ic"
          placeholder="Filtrar comodidades..."
        />
        <div
          class="max-h-[500px] overflow-y-auto border border-slate-200 dark:border-slate-600 rounded-lg p-3"
        >
          <div
            v-for="(items, category) in filteredCategories"
            :key="category"
            class="mb-4 last:mb-0"
          >
            <h4
              class="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2 px-2"
            >
              {{ category }}
            </h4>
            <div class="grid grid-cols-2 gap-1">
              <label
                v-for="amenity in items"
                :key="amenity"
                class="flex items-center gap-2 py-1 px-2 rounded text-sm cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700"
                :class="
                  form.property_amenities.includes(amenity)
                    ? 'text-woot-600 font-medium bg-woot-50 dark:bg-woot-900/20'
                    : 'text-slate-700 dark:text-slate-300'
                "
              >
                <input
                  type="checkbox"
                  :checked="form.property_amenities.includes(amenity)"
                  class="rounded border-slate-300 text-woot-500"
                  @change="toggleAmenity(amenity)"
                />
                {{ amenity }}
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- POLÍTICAS DE IDADE -->
      <div v-show="activeTab === 'age'" class="flex flex-col gap-4 max-w-3xl">
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
            >Como será aplicada as políticas de idade na reserva?</label>
          <select v-model="form.age_policy_mode" :class="ic">
            <option v-for="m in agePolicyModes" :key="m.value" :value="m.value">
              {{ m.label }}
            </option>
          </select>
        </div>

        <div
          class="border border-slate-200 dark:border-slate-600 rounded-lg p-4"
        >
          <div class="flex items-center justify-between mb-3">
            <h4
              class="text-sm font-semibold text-slate-800 dark:text-slate-100"
            >
              Política de idade padrão
            </h4>
            <button
              type="button"
              class="text-xs text-woot-500 hover:underline"
              @click="addAgeRange"
            >
              + Adicionar Faixa
            </button>
          </div>
          <div
            v-for="(range, idx) in form.age_ranges"
            :key="idx"
            class="flex items-center gap-2 mb-2 p-2 bg-slate-50 dark:bg-slate-700 rounded-lg"
          >
            <input
              v-model.number="range.min_age"
              type="number"
              min="0"
              class="w-16 px-2 py-1 border border-slate-200 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-800 text-center"
            />
            <span class="text-sm text-slate-500">até</span>
            <input
              v-model.number="range.max_age"
              type="number"
              min="0"
              class="w-16 px-2 py-1 border border-slate-200 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-800 text-center"
            />
            <span class="text-sm text-slate-500 whitespace-nowrap">Como cobrar</span>
            <select
              v-model="range.charge_type"
              class="px-2 py-1 border border-slate-200 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-800"
            >
              <option
                v-for="ct in chargeTypes"
                :key="ct.value"
                :value="ct.value"
              >
                {{ ct.label }}
              </option>
            </select>
            <button
              type="button"
              class="text-xs text-red-500 hover:text-red-700"
              @click="removeAgeRange(idx)"
            >
              Remover
            </button>
          </div>
        </div>

        <div
          class="border border-slate-200 dark:border-slate-600 rounded-lg p-4"
        >
          <h4
            class="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3"
          >
            Grátis por Adulto
          </h4>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-xs text-slate-500 mb-1">Adultos necessários</label><input
                v-model.number="form.free_adults_required"
                type="number"
                min="1"
                :class="ic"
              />
            </div>
            <div>
              <label class="block text-xs text-slate-500 mb-1">Crianças grátis</label><input
                v-model.number="form.free_children_per_adult"
                type="number"
                min="0"
                :class="ic"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- MEIOS DE PAGAMENTO -->
      <div
        v-show="activeTab === 'payment'"
        class="flex flex-col gap-4 max-w-3xl"
      >
        <!-- Cartão de Crédito -->
        <div
          class="border border-slate-200 dark:border-slate-600 rounded-lg p-4"
        >
          <label
            class="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3"
          >
            <input
              v-model="form.credit_card_enabled"
              type="checkbox"
              class="rounded border-slate-300 text-woot-500"
            />
            Permitir pagamento com cartão de crédito
          </label>
          <div v-if="form.credit_card_enabled" class="flex flex-col gap-3">
            <div>
              <label class="block text-xs text-slate-500 mb-2">Tipos de cartão aceitos</label>
              <div class="flex flex-wrap gap-2">
                <label
                  v-for="brand in cardBrands"
                  :key="brand"
                  class="flex items-center gap-1.5 px-3 py-1.5 border rounded-lg cursor-pointer text-xs font-medium transition-colors"
                  :class="
                    form.accepted_card_brands.includes(brand)
                      ? 'border-woot-500 bg-woot-50 text-woot-600 dark:bg-woot-900/20'
                      : 'border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:border-slate-400'
                  "
                >
                  <input
                    type="checkbox"
                    :checked="form.accepted_card_brands.includes(brand)"
                    class="rounded border-slate-300 text-woot-500 w-3.5 h-3.5"
                    @change="toggleCardBrand(brand)"
                  />
                  {{ brand }}
                </label>
              </div>
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-xs text-slate-500 mb-1">Máximo de parcelas</label><input
                  v-model.number="form.credit_card_installments"
                  type="number"
                  min="1"
                  max="12"
                  :class="ic"
                />
              </div>
              <div>
                <label class="block text-xs text-slate-500 mb-1">Parcela mínima (R$)</label><input
                  v-model.number="form.credit_card_min_installment"
                  type="number"
                  min="0"
                  step="0.01"
                  :class="ic"
                />
              </div>
            </div>
            <label
              class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
            >
              <input
                v-model="form.include_extras_in_card"
                type="checkbox"
                class="rounded border-slate-300 text-woot-500"
              />
              Incluir valor dos extras nas cobranças via cartão
            </label>
            <label
              class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
            >
              <input
                v-model="form.antifraud_enabled"
                type="checkbox"
                class="rounded border-slate-300 text-woot-500"
              />
              Habilitar sistema de Anti-Fraude
            </label>
          </div>
        </div>

        <!-- Depósito -->
        <div
          class="border border-slate-200 dark:border-slate-600 rounded-lg p-4"
        >
          <label
            class="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100"
          >
            <input
              v-model="form.deposit_enabled"
              type="checkbox"
              class="rounded border-slate-300 text-woot-500"
            />
            Permitir pagamento por depósito
          </label>
        </div>

        <!-- PIX -->
        <div
          class="border border-slate-200 dark:border-slate-600 rounded-lg p-4"
        >
          <label
            class="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3"
          >
            <input
              v-model="form.pix_enabled"
              type="checkbox"
              class="rounded border-slate-300 text-woot-500"
            />
            Permitir pagamento por PIX
          </label>
          <div v-if="form.pix_enabled" class="flex flex-col gap-3">
            <label
              class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
            >
              <input
                v-model="form.include_extras_in_pix"
                type="checkbox"
                class="rounded border-slate-300 text-woot-500"
              />
              Incluir valor dos extras nas cobranças via PIX
            </label>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-xs text-slate-500 mb-1">Habilitar apenas para reservas com antecedência mínima de
                  (dias)</label>
                <input
                  v-model.number="form.pix_min_advance_days"
                  type="number"
                  min="0"
                  :class="ic"
                />
              </div>
              <div>
                <label class="block text-xs text-slate-500 mb-1">Tempo de expiração QR Code (min)</label>
                <input
                  v-model.number="form.pix_qr_expiration_minutes"
                  type="number"
                  min="1"
                  :class="ic"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Direto no Hotel -->
        <div
          class="border border-slate-200 dark:border-slate-600 rounded-lg p-4"
        >
          <label
            class="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100"
          >
            <input
              v-model="form.direct_hotel_enabled"
              type="checkbox"
              class="rounded border-slate-300 text-woot-500"
            />
            Permitir pagamento direto no hotel
          </label>
        </div>

      </div>

      <!-- INTEGRAÇÕES -->
      <div
        v-show="activeTab === 'integrations'"
        class="flex flex-col gap-4 max-w-3xl"
      >
        <!-- Scripts de Rastreamento -->
        <div
          class="border border-slate-200 dark:border-slate-600 rounded-lg p-4"
        >
          <h4
            class="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1"
          >
            Scripts de Rastreamento
          </h4>
          <p class="text-xs text-slate-500 mb-4">
            Adicione seus IDs de rastreamento para acompanhar conversões e
            visitantes no motor de reservas.
          </p>
          <div class="flex flex-col gap-3">
            <div>
              <label class="block text-xs text-slate-500 mb-1"
                >Google Analytics (GTAG ID)</label
              >
              <input
                v-model="form.integrations_config.gtag_id"
                type="text"
                :class="ic"
                class="w-full"
                placeholder="G-XXXXXXXXXX"
              />
            </div>
            <div>
              <label class="block text-xs text-slate-500 mb-1"
                >Google Tag Manager (GTM ID)</label
              >
              <input
                v-model="form.integrations_config.gtm_id"
                type="text"
                :class="ic"
                class="w-full"
                placeholder="GTM-XXXXXXX"
              />
            </div>
            <div>
              <label class="block text-xs text-slate-500 mb-1"
                >Facebook Pixel ID</label
              >
              <input
                v-model="form.integrations_config.facebook_pixel_id"
                type="text"
                :class="ic"
                class="w-full"
                placeholder="000000000000000"
              />
            </div>
          </div>
        </div>

        <!-- Script Universal -->
        <div
          class="border border-slate-200 dark:border-slate-600 rounded-lg p-4"
        >
          <h4
            class="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1"
          >
            Script Universal
          </h4>
          <p class="text-xs text-slate-500 mb-4">
            Insira qualquer script personalizado que será incluído no
            &lt;head&gt; do motor de reservas. Use com cuidado.
          </p>
          <textarea
            v-model="form.integrations_config.universal_script"
            :class="ic"
            class="w-full font-mono text-xs"
            rows="5"
            placeholder="<script>...</script>"
          />
        </div>

        <!-- Metasearchs -->
        <div
          class="border border-slate-200 dark:border-slate-600 rounded-lg p-4"
        >
          <h4
            class="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1"
          >
            Metasearchs
          </h4>
          <p class="text-xs text-slate-500 mb-4">
            Configure integrações com metabuscadores de hotéis para aumentar sua
            visibilidade.
          </p>
          <div class="flex flex-col gap-4">
            <!-- TripAdvisor -->
            <div
              class="border border-slate-100 dark:border-slate-700 rounded-lg p-3"
            >
              <label
                class="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200 mb-3"
              >
                <input
                  v-model="form.integrations_config.tripadvisor_enabled"
                  type="checkbox"
                  class="rounded border-slate-300 text-woot-500"
                />
                TripAdvisor
              </label>
              <div
                v-if="form.integrations_config.tripadvisor_enabled"
                class="grid grid-cols-2 gap-3"
              >
                <div>
                  <label class="block text-xs text-slate-500 mb-1"
                    >Partner ID</label
                  >
                  <input
                    v-model="form.integrations_config.tripadvisor_partner_id"
                    type="text"
                    :class="ic"
                    class="w-full"
                    placeholder="ID do parceiro"
                  />
                </div>
                <div>
                  <label class="block text-xs text-slate-500 mb-1"
                    >Hotel ID</label
                  >
                  <input
                    v-model="form.integrations_config.tripadvisor_hotel_id"
                    type="text"
                    :class="ic"
                    class="w-full"
                    placeholder="ID do hotel"
                  />
                </div>
              </div>
            </div>

            <!-- Trivago -->
            <div
              class="border border-slate-100 dark:border-slate-700 rounded-lg p-3"
            >
              <label
                class="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200 mb-3"
              >
                <input
                  v-model="form.integrations_config.trivago_enabled"
                  type="checkbox"
                  class="rounded border-slate-300 text-woot-500"
                />
                Trivago
              </label>
              <div
                v-if="form.integrations_config.trivago_enabled"
                class="grid grid-cols-2 gap-3"
              >
                <div>
                  <label class="block text-xs text-slate-500 mb-1"
                    >Partner ID</label
                  >
                  <input
                    v-model="form.integrations_config.trivago_partner_id"
                    type="text"
                    :class="ic"
                    class="w-full"
                    placeholder="ID do parceiro"
                  />
                </div>
                <div>
                  <label class="block text-xs text-slate-500 mb-1"
                    >Hotel ID</label
                  >
                  <input
                    v-model="form.integrations_config.trivago_hotel_id"
                    type="text"
                    :class="ic"
                    class="w-full"
                    placeholder="ID do hotel"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Gateway Asaas -->
        <div
          class="border border-slate-200 dark:border-slate-600 rounded-lg p-4"
        >
          <h4
            class="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1"
          >
            Gateway de Pagamento — Asaas
          </h4>
          <p class="text-xs text-slate-500 mb-4">
            Configure sua chave de API do Asaas para processar cobranças PIX,
            cartão e boleto automaticamente no motor de reservas.
          </p>
          <div class="flex flex-col gap-3">
            <div>
              <label class="block text-xs text-slate-500 mb-1"
                >Chave de API (access_token)</label
              >
              <input
                v-model="form.asaas_api_key"
                type="password"
                :class="ic"
                class="w-full"
                placeholder="$aact_..."
              />
            </div>
            <div>
              <label class="block text-xs text-slate-500 mb-1">Ambiente</label>
              <select v-model="form.asaas_environment" :class="ic">
                <option value="production">Produção</option>
                <option value="sandbox">Sandbox (Testes)</option>
              </select>
            </div>
            <div class="flex items-center gap-3">
              <Button
                label="Testar Conexão"
                variant="secondary"
                size="small"
                :is-loading="asaasTestLoading"
                @click="testAsaasConnection"
              />
              <span
                v-if="asaasTestResult === 'success'"
                class="text-xs text-emerald-600 flex items-center gap-1"
              >
                <span class="material-icons" style="font-size: 14px"
                  >check_circle</span
                >
                Conectado!
              </span>
              <span
                v-if="asaasTestResult === 'error'"
                class="text-xs text-red-500 flex items-center gap-1"
              >
                <span class="material-icons" style="font-size: 14px"
                  >error</span
                >
                {{ asaasTestError }}
              </span>
            </div>
            <div
              v-if="asaasTestData"
              class="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3"
            >
              <p
                class="text-xs font-semibold text-emerald-700 dark:text-emerald-300 mb-2"
              >
                Conta Asaas conectada
              </p>
              <div
                class="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600 dark:text-slate-300"
              >
                <span v-if="asaasTestData.name"
                  ><strong>Nome:</strong> {{ asaasTestData.name }}</span
                >
                <span v-if="asaasTestData.email"
                  ><strong>Email:</strong> {{ asaasTestData.email }}</span
                >
                <span v-if="asaasTestData.wallet_id"
                  ><strong>Wallet:</strong> {{ asaasTestData.wallet_id }}</span
                >
                <span v-if="asaasTestData.balance !== undefined"
                  ><strong>Saldo:</strong> R$
                  {{
                    Number(asaasTestData.balance).toLocaleString('pt-BR', {
                      minimumFractionDigits: 2,
                    })
                  }}</span
                >
                <span
                  ><strong>Ambiente:</strong>
                  {{
                    asaasTestData.environment === 'sandbox'
                      ? 'Sandbox'
                      : 'Produção'
                  }}</span
                >
              </div>
            </div>
            <div
              v-if="form.asaas_api_key && !asaasTestData"
              class="text-xs text-emerald-600 flex items-center gap-1"
            >
              <span class="material-icons" style="font-size: 14px"
                >check_circle</span
              >
              Chave configurada — cobranças serão geradas automaticamente via
              Asaas
            </div>
            <p class="text-xs text-slate-400">
              URL do Webhook para configurar no painel Asaas:
              <code
                class="bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded text-xs"
              >
                {{ webhookUrl }}
              </code>
            </p>
          </div>
        </div>
      </div>

      <!-- TAXAS -->
      <div v-show="activeTab === 'taxes'" class="flex flex-col gap-4 max-w-3xl">
        <div class="flex items-center justify-between">
          <p class="text-sm text-slate-600 dark:text-slate-300">
            {{
              form.taxes_config.length
                ? form.taxes_config.length + ' taxa(s) configurada(s)'
                : 'Nenhuma taxa criada para esta propriedade ainda.'
            }}
          </p>
          <Button label="+ Adicionar Taxa" size="sm" @click="addTax" />
        </div>
        <div
          v-for="(tax, idx) in form.taxes_config"
          :key="idx"
          class="border border-slate-200 dark:border-slate-600 rounded-lg p-4"
        >
          <div class="flex items-center justify-between mb-3">
            <h4
              class="text-sm font-semibold text-slate-800 dark:text-slate-100"
            >
              Taxa {{ idx + 1 }}
            </h4>
            <button
              type="button"
              class="text-xs text-red-500 hover:text-red-700"
              @click="removeTax(idx)"
            >
              Remover
            </button>
          </div>
          <div class="grid grid-cols-2 gap-4 mb-3">
            <div>
              <label class="block text-xs text-slate-500 mb-1">Nome *</label><input
                v-model="tax.name"
                type="text"
                :class="ic"
                placeholder="Ex: ISS, Taxa de Turismo"
              />
            </div>
            <div>
              <label class="block text-xs text-slate-500 mb-1">Valor</label><input
                v-model.number="tax.percentage"
                type="number"
                min="0"
                step="0.01"
                :class="ic"
              />
            </div>
          </div>
          <div class="grid grid-cols-2 gap-4 mb-3">
            <div>
              <label class="block text-xs text-slate-500 mb-1">Formato do Valor</label>
              <div class="flex gap-3">
                <label
                  class="flex items-center gap-1 text-sm text-slate-700 dark:text-slate-200"
                  ><input
                    v-model="tax.value_format"
                    type="radio"
                    value="fixed"
                  />
                  Fixo</label>
                <label
                  class="flex items-center gap-1 text-sm text-slate-700 dark:text-slate-200"
                  ><input
                    v-model="tax.value_format"
                    type="radio"
                    value="percentage"
                  />
                  Porcentagem</label>
              </div>
            </div>
            <div>
              <label class="block text-xs text-slate-500 mb-1">Inclusa na tarifa?</label>
              <div class="flex gap-3">
                <label
                  class="flex items-center gap-1 text-sm text-slate-700 dark:text-slate-200"
                  ><input
                    v-model="tax.included_in_rate"
                    type="radio"
                    :value="false"
                  />
                  Não</label>
                <label
                  class="flex items-center gap-1 text-sm text-slate-700 dark:text-slate-200"
                  ><input
                    v-model="tax.included_in_rate"
                    type="radio"
                    :value="true"
                  />
                  Sim</label>
              </div>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-4 mb-3">
            <div>
              <label class="block text-xs text-slate-500 mb-1">Por Noite ou Reserva</label>
              <div class="flex gap-3">
                <label
                  class="flex items-center gap-1 text-sm text-slate-700 dark:text-slate-200"
                  ><input
                    v-model="tax.per_night_or_stay"
                    type="radio"
                    value="night"
                  />
                  Noite</label>
                <label
                  class="flex items-center gap-1 text-sm text-slate-700 dark:text-slate-200"
                  ><input
                    v-model="tax.per_night_or_stay"
                    type="radio"
                    value="stay"
                  />
                  Reserva</label>
              </div>
            </div>
            <div>
              <label class="block text-xs text-slate-500 mb-1">Por Quarto ou Pessoa</label>
              <div class="flex gap-3">
                <label
                  class="flex items-center gap-1 text-sm text-slate-700 dark:text-slate-200"
                  ><input
                    v-model="tax.per_room_or_person"
                    type="radio"
                    value="room"
                  />
                  Quarto</label>
                <label
                  class="flex items-center gap-1 text-sm text-slate-700 dark:text-slate-200"
                  ><input
                    v-model="tax.per_room_or_person"
                    type="radio"
                    value="person"
                  />
                  Pessoa</label>
              </div>
            </div>
          </div>
          <div>
            <label class="block text-xs text-slate-500 mb-1">Cobrar no pré-pagamento?</label>
            <div class="flex gap-3">
              <label
                class="flex items-center gap-1 text-sm text-slate-700 dark:text-slate-200"
                ><input
                  v-model="tax.charge_in_prepayment"
                  type="radio"
                  :value="true"
                />
                Sim</label>
              <label
                class="flex items-center gap-1 text-sm text-slate-700 dark:text-slate-200"
                ><input
                  v-model="tax.charge_in_prepayment"
                  type="radio"
                  :value="false"
                />
                Não, cobrada no hotel</label>
            </div>
          </div>
          <div class="mt-3">
            <label class="block text-xs text-slate-500 mb-1">Descrição</label><textarea v-model="tax.description" rows="2" :class="ic" />
          </div>
        </div>
      </div>

      <!-- ORÇAMENTO NA CONVERSA -->
      <div
        v-show="activeTab === 'orcamento'"
        class="flex flex-col gap-4 max-w-3xl"
      >
        <div
          class="border-2 border-woot-500/30 bg-woot-500/5 dark:bg-slate-800 rounded-lg p-4 flex flex-col gap-4"
        >
          <div class="flex items-center gap-3">
            <span class="text-2xl">💬</span>
            <div>
              <h4
                class="text-sm font-semibold text-slate-800 dark:text-slate-100"
              >
                Orçamento pelo Motor de Reserva na Conversa
              </h4>
              <p class="text-xs text-slate-500 mt-0.5">
                Quando habilitado, a aba Hospedagem nas conversas usará os
                quartos, tarifas e extras cadastrados no Motor de Reserva para
                gerar orçamentos com cálculo automático de preços.
              </p>
            </div>
          </div>
          <div
            class="flex items-center gap-3 p-3 rounded-lg"
            :class="
              form.orcamento_conversa_enabled
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                : 'bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600'
            "
          >
            <input
              id="orcamento-conversa"
              v-model="form.orcamento_conversa_enabled"
              type="checkbox"
              class="rounded border-slate-300 text-woot-500 focus:ring-woot-500"
            />
            <label
              for="orcamento-conversa"
              class="text-sm font-medium"
              :class="
                form.orcamento_conversa_enabled
                  ? 'text-green-700 dark:text-green-400'
                  : 'text-slate-600 dark:text-slate-300'
              "
            >
              Habilitar orçamento via Motor de Reserva nas conversas
            </label>
          </div>
        </div>

        <div v-if="form.orcamento_conversa_enabled" class="flex flex-col gap-4">
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
            >
              Template da mensagem de orçamento (opcional)
            </label>
            <p class="text-xs text-slate-500 mb-2">
              Personalize a mensagem enviada na conversa. Deixe em branco para
              usar o formato padrão. Variáveis disponíveis:
              <code class="text-woot-500">{id}</code>,
              <code class="text-woot-500">{quarto}</code>,
              <code class="text-woot-500">{tarifa}</code>,
              <code class="text-woot-500">{adultos}</code>,
              <code class="text-woot-500">{criancas}</code>,
              <code class="text-woot-500">{checkin}</code>,
              <code class="text-woot-500">{checkout}</code>,
              <code class="text-woot-500">{diarias}</code>,
              <code class="text-woot-500">{valor_diaria}</code>,
              <code class="text-woot-500">{valor_extras}</code>,
              <code class="text-woot-500">{valor_total}</code>,
              <code class="text-woot-500">{extras}</code>,
              <code class="text-woot-500">{observacoes}</code>,
              <code class="text-woot-500">{hotel}</code>,
              <code class="text-woot-500">{checkin_hora}</code>,
              <code class="text-woot-500">{checkout_hora}</code>,
              <code class="text-woot-500">{pensao}</code>
            </p>
            <textarea
              v-model="form.orcamento_message_template"
              rows="12"
              :class="ic"
              placeholder="🏨 *Orçamento de Hospedagem #{id}*

📋 *Quarto:* {quarto}
📊 *Tarifa:* {tarifa}
🍽️ *Pensão:* {pensao}

👤 *Adultos:* {adultos}
👶 *Crianças:* {criancas}

📅 *Check-in:* {checkin} às {checkin_hora}
📅 *Check-out:* {checkout} às {checkout_hora}
🌙 *Diárias:* {diarias}

💰 *Hospedagem:* {valor_diaria}
{extras}
✅ *TOTAL: {valor_total}*

🏨 {hotel}
{observacoes}"
            />
          </div>

          <div
            class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3"
          >
            <p class="text-xs text-blue-700 dark:text-blue-400">
              <strong>Como funciona:</strong> Na aba Hospedagem de cada
              conversa, o atendente poderá selecionar quarto, tarifa e extras do
              Motor de Reserva. O sistema calculará o preço automaticamente e o
              atendente poderá enviar o orçamento formatado diretamente na
              conversa.
            </p>
          </div>
        </div>
      </div>

      <div
        class="flex justify-end pt-4 border-t border-slate-200 dark:border-slate-600"
      >
        <Button
          label="Salvar Configurações"
          :is-loading="isSubmitting"
          @click="saveConfig"
        />
      </div>
    </template>
  </div>
</template>
